import axios from 'axios';
import useCctvStatus from './useCctvStatus';
import { useCallback, useEffect } from 'react';
import { useStreamTokenRefresh } from './useStreamTokenRefresh';

interface UseCctvStatusCheckerProps {
    hlsUrl?: string;
    webrtcUrl?: string;
    clientId?: string;
    projectId?: string;
    cameraId?: string;
}

type UseCctvStatusCheckerInput = string | UseCctvStatusCheckerProps;

const useCctvStatusChecker = (input: UseCctvStatusCheckerInput) => {
    const { isLoading, isOffline, isOnline, setOnline, setOffline, status } = useCctvStatus();

    // Handle backward compatibility: accept string or object
    let hlsUrlProp: string | undefined;
    let webrtcUrlProp: string | undefined;
    let clientId: string | undefined;
    let projectId: string | undefined;
    let cameraId: string | undefined;

    if (typeof input === 'string') {
        hlsUrlProp = input;
    } else {
        hlsUrlProp = input.hlsUrl;
        webrtcUrlProp = input.webrtcUrl;
        clientId = input.clientId;
        projectId = input.projectId;
        cameraId = input.cameraId;
    }

    // Only use token refresh if we have projectId and cameraId (new behavior)
    const {
        hlsUrl: updatedHlsUrl,
        webrtcUrl: updatedWebrtcUrl,
        refetch: refetchToken,
    } = useStreamTokenRefresh(clientId, projectId, cameraId, hlsUrlProp, webrtcUrlProp);

    // Use updated URLs if available (with token refresh), otherwise use original URLs
    const streamUrl = updatedHlsUrl || updatedWebrtcUrl || hlsUrlProp || webrtcUrlProp;

    const checkStatus = useCallback(async () => {
        if (!streamUrl) {
            setOffline();
            return;
        }

        try {
            await axios.get(streamUrl, { timeout: 5000 });
            setOnline();
        } catch (ex) {
            setOffline();

            const errorStatus = (ex as any)?.response?.status;
            if (errorStatus === 401) {
                console.log('Token expired (401), refreshing...');
                refetchToken();
            }
        }
    }, [streamUrl, setOnline, setOffline, refetchToken]);

    useEffect(() => {
        checkStatus();
    }, [streamUrl, checkStatus]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            checkStatus();
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, [streamUrl, checkStatus]);

    return {
        isLoading,
        isOffline,
        isOnline,
        status,
    };
};

export default useCctvStatusChecker;
